"use client";!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();

import { useRef, useState, useTransition } from "react"
import { useApolloClient } from "@apollo/client/react"
import searchGeographicLandingGroups from "../../../graphql/queries/public/searchGeographicLandingGroups"
import { uniqueGroups } from "../../../lib/landingPages/filters"
import { readGeographicGroupPage } from "../../../lib/landingPages/geographicGroups"
import type { LinkField, RichTextField } from "../../../lib/content/legacyBlockTypes"
import type {
  GeographicGroupResult,
  GeographicGroupsData,
  GeographicLandingVariables,
  GeographicListingScope,
} from "../../../lib/landingPages/types"
import { LandingEmptyState, LandingGroupGrid } from "./LandingGroupSection"
import {
  LandingCountLabel,
  LandingHeadingBlock,
  LandingSection,
  landingSecondaryButtonClass,
} from "./LandingStylePrimitives"

type GeographicGroupListingsProps = {
  scope: GeographicListingScope
  heading: string
  intro?: RichTextField | null
  variables: GeographicLandingVariables | null
  initialResult: GeographicGroupResult
  emptyHeading?: string | null
  emptyBody?: RichTextField | null
  emptyCtaLabel?: string | null
  emptyCtaLink?: LinkField | null
}

export default function GeographicGroupListings({
  scope,
  heading,
  intro,
  variables,
  initialResult,
  emptyHeading,
  emptyBody,
  emptyCtaLabel,
  emptyCtaLink,
}: GeographicGroupListingsProps) {
  const client = useApolloClient()
  const [result, setResult] = useState(initialResult)
  const [requestFailed, setRequestFailed] = useState(false)
  const [isPending, startTransition] = useTransition()
  const requestInFlight = useRef(false)
  const page = result.status === "success" ? result : null
  const hasError = result.status === "error" || requestFailed

  function loadGroups() {
    if (!variables || requestInFlight.current) return
    requestInFlight.current = true
    setRequestFailed(false)

    startTransition(async () => {
      const after = page?.endCursor || undefined
      try {
        const { data } = await client.query<GeographicGroupsData>({
          query: searchGeographicLandingGroups,
          variables: { ...variables, ...(after ? { after } : {}) },
          fetchPolicy: "no-cache",
          errorPolicy: "none",
          context: { public: true },
        })
        const next = readGeographicGroupPage(data?.searchGroupsPublicLanding)
        if (after && next.hasNextPage && next.endCursor === after) {
          throw new Error("Group pagination did not advance")
        }
        setResult({
          status: "success",
          ...next,
          groups: uniqueGroups([...(page?.groups || []), ...next.groups]),
        })
      } catch {
        // Keep the last successful page and cursor so retry resumes this section.
        setRequestFailed(true)
      } finally {
        requestInFlight.current = false
      }
    })
  }

  return (
    <LandingSection
      id={`groups-${scope}`}
      aria-label={heading}
      aria-busy={isPending}
    >
      <LandingHeadingBlock heading={heading} body={intro}>
        <div role="status" aria-live="polite" aria-atomic="true" className="mt-4">
          {page ? (
            <LandingCountLabel>
              Showing {page.groups.length} of {page.totalCount} therapy{" "}
              {page.totalCount === 1 ? "group" : "groups"}
            </LandingCountLabel>
          ) : null}
        </div>
      </LandingHeadingBlock>

      {page?.groups.length ? (
        <LandingGroupGrid groups={page.groups} layout="grid" geographicListing />
      ) : page ? (
        <div className="mt-7">
          <LandingEmptyState
            heading={
              emptyHeading ||
              (scope === "local"
                ? "No in-person therapy groups are listed here yet"
                : "No online therapy groups list eligibility for this state yet")
            }
            body={emptyBody}
            ctaLabel={emptyCtaLabel}
            ctaLink={emptyCtaLink}
          />
        </div>
      ) : null}

      {hasError ? (
        <p role="alert" className="mt-6 text-[17px] leading-relaxed text-[#3f4649]">
          {page
            ? "We couldn’t load more groups. Your current results are still shown."
            : "We couldn’t load these groups. Please try again."}
        </p>
      ) : null}

      {variables && (page?.hasNextPage || hasError) ? (
        <div className="mt-7 flex justify-center">
          <button
            type="button"
            onClick={loadGroups}
            disabled={isPending}
            aria-label={
              hasError
                ? `Retry loading ${scope} therapy groups`
                : `Load more ${scope} therapy groups`
            }
            className={`${landingSecondaryButtonClass} disabled:cursor-wait disabled:opacity-60`}
          >
            {isPending
              ? "Loading groups…"
              : hasError
                ? "Try again"
                : "Load more groups"}
          </button>
        </div>
      ) : null}
    </LandingSection>
  )
}
